import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "semantic-ui-react";

import { listPurProductStock, PurProductStockRow } from "react-lib/apis/PUR/purStockApi";
import { PurClientConfig } from "react-lib/apis/PUR/purRead";

import { formatMeerpMessage, purErrorMessageId } from "./purPresentation";

interface PurProductStockProps {
  client: PurClientConfig;
  productId: number | null;
}
const PAGE_SIZE = 20;
// Same advisory window as the stock management screen's expiry remark.
const EXPIRING_MONTHS = 6;

const COLUMN_IDS = [
  "MEERP_STOCK_COL_TYPE", "MEERP_STOCK_COL_CODE", "MEERP_STOCK_COL_NAME", "MEERP_STOCK_COL_STORAGE",
  "MEERP_STOCK_COL_BIN", "MEERP_STOCK_COL_TOTAL", "MEERP_STOCK_COL_UNEXP", "MEERP_STOCK_COL_GRAND",
  "MEERP_STOCK_COL_MIN", "MEERP_STOCK_COL_MAX", "MEERP_STOCK_COL_REMARK", "MEERP_STOCK_COL_ACTIVE",
] as const;

// Stock aggregates belong to the shared core stock system; MeERP only reads.
const isBelowReorderPoint = (row: PurProductStockRow): boolean =>
  row.unexpQty !== null && row.minQty !== null && Number(row.unexpQty) < Number(row.minQty);

const isNearExpiry = (row: PurProductStockRow, now: Date): boolean => {
  if (row.minExpiry === null) return false;
  const expiry = new Date(row.minExpiry);
  if (Number.isNaN(expiry.getTime())) return false;
  const horizon = new Date(now);
  horizon.setMonth(horizon.getMonth() + EXPIRING_MONTHS);
  return expiry.getTime() <= horizon.getTime();
};

// Mirrors the stock management screen: active stock rows, or an active product.
const isActiveRow = (row: PurProductStockRow): boolean =>
  (row.activeCount !== null && row.activeCount > 0) ||
  (row.activeFlag !== null && row.activeFlag !== 0 && row.activeFlag !== 5);

const PurProductStock: React.FC<PurProductStockProps> = ({ client, productId }) => {
  const intl = useIntl();
  // A long-lived host IntlProvider may predate these keys; fall back to the
  // MeERP catalogue so raw message IDs never reach the screen.
  const t = (messageId: string) => formatMeerpMessage(intl, messageId);
  const [request, setRequest] = useState<{ productId: number; offset: number; revision: number } | null>(null);
  const [page, setPage] = useState<{ count: number; records: PurProductStockRow[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorId, setErrorId] = useState<string | null>(null);

  // Selecting another medicine (or a new client context) restarts at page one.
  useEffect(() => {
    if (productId === null) {
      setRequest(null); setPage(null); setErrorId(null);
      return;
    }
    setRequest((previous) => ({ productId, offset: 0, revision: (previous?.revision || 0) + 1 }));
  }, [client, productId]);

  useEffect(() => {
    if (!request) return;
    let active = true;
    setLoading(true); setErrorId(null); setPage(null);
    listPurProductStock(client, { productId: request.productId, limit: PAGE_SIZE, offset: request.offset })
      .then((result) => { if (active) setPage(result); })
      .catch((error: unknown) => { if (active) setErrorId(purErrorMessageId(error)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, request]);

  const quantity = (value: string | null): string => {
    if (value === null) return t("MEERP_UNKNOWN");
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toLocaleString("en-US") : t("MEERP_UNKNOWN");
  };
  const reload = () => {
    if (request) setRequest((previous) => ({ productId: previous!.productId, offset: 0, revision: previous!.revision + 1 }));
  };

  if (productId === null) return null;

  return <section className="card-meerp__stock" aria-label={t("MEERP_STOCK_SECTION")}>
    <div className="card-meerp__stock-head">
      <h4>{t("MEERP_STOCK_SECTION")}</h4>
      <Button type="button" size="mini" disabled={loading || !request} onClick={reload}>{t("MEERP_STOCK_RELOAD")}</Button>
    </div>
    {loading && <small role="status">{t("MEERP_LOADING")}</small>}
    {errorId && <small role="alert" className="card-meerp__error-text">{t(errorId)}</small>}
    {page && !page.records.length && <small>{t("MEERP_STOCK_EMPTY")}</small>}
    {page && page.records.length > 0 && <>
      <div className="card-meerp__table-wrap"><table className="card-meerp__table">
        <caption className="card-meerp__sr-only">{t("MEERP_STOCK_SECTION")}</caption>
        <thead><tr>{COLUMN_IDS.map((id) => <th scope="col" key={id}>{t(id)}</th>)}</tr></thead>
        <tbody>{page.records.map((row, index) => {
          const remarks = [isBelowReorderPoint(row) ? t("MEERP_STOCK_REMARK_LOW") : null, isNearExpiry(row, new Date()) ? t("MEERP_STOCK_REMARK_EXPIRING") : null].filter(Boolean);
          return <tr key={`${row.storageId ?? "s"}-${row.productId}-${index}`}>
            <td>{row.productType || t("MEERP_UNKNOWN")}</td>
            <td>{row.code || t("MEERP_UNKNOWN")}</td>
            <td>{row.name || t("MEERP_UNKNOWN")}</td>
            <td>{row.storageName || (row.storageId ? `#${row.storageId}` : t("MEERP_UNKNOWN"))}</td>
            <td>{row.binLocation || t("MEERP_UNKNOWN")}</td>
            <td>{quantity(row.totalQty)}</td>
            <td>{quantity(row.unexpQty)}</td>
            <td>{quantity(row.grandQty)}</td>
            <td>{quantity(row.minQty)}</td>
            <td>{quantity(row.maxQty)}</td>
            <td>{remarks.length ? remarks.join(" · ") : t("MEERP_UNKNOWN")}</td>
            <td aria-label={isActiveRow(row) ? t("MEERP_STOCK_ACTIVE_ON") : t("MEERP_STOCK_ACTIVE_OFF")}>{isActiveRow(row) ? "✓" : t("MEERP_UNKNOWN")}</td>
          </tr>;
        })}</tbody>
      </table></div>
      <small>{t("MEERP_STOCK_NOTE")}</small>
      {request && <div className="card-meerp__lookup-pager">
        <Button type="button" size="mini" disabled={loading || request.offset === 0} onClick={() => setRequest((previous) => previous && { ...previous, offset: Math.max(0, previous.offset - PAGE_SIZE) })}>{t("MEERP_PREVIOUS")}</Button>
        <small>{formatMeerpMessage(intl, "MEERP_PAGE", { current: Math.floor(request.offset / PAGE_SIZE) + 1, total: Math.max(1, Math.ceil(page.count / PAGE_SIZE)) })}</small>
        <Button type="button" size="mini" disabled={loading || request.offset + PAGE_SIZE >= page.count} onClick={() => setRequest((previous) => previous && { ...previous, offset: previous.offset + PAGE_SIZE })}>{t("MEERP_NEXT")}</Button>
      </div>}
    </>}
  </section>;
};

export default PurProductStock;
